import React, { useMemo } from 'react';
import { FileText } from 'lucide-react';
import { OrderSheet } from '../../../types';
import {
  getOrderItemAggregationKey,
  getOrderItemDisplayName,
} from '../../../utils/orderItemDisplay';

const LABELS_PER_PAGE = 16;

export const PACKING_LABEL_STYLES = `
  .packing-label-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    overflow-x: auto;
    padding-bottom: 16px;
  }
  .packing-label-page {
    width: 210mm;
    height: 297mm;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: 99.1mm 99.1mm;
    grid-auto-rows: 33.9mm;
    column-gap: 2.5mm;
    row-gap: 0;
    padding: 14mm 4mm 10.4mm 4.8mm;
    overflow: hidden;
    background: white;
    border: 1px solid #e5e7eb;
    box-shadow: 0 20px 45px rgba(15, 23, 42, 0.12);
  }
  .packing-label-cell {
    width: 99.1mm;
    height: 33.9mm;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: stretch;
    column-gap: var(--label-column-gap, 3mm);
    overflow: hidden;
    padding: var(--label-cell-padding, 2.6mm 4mm 2mm);
    border: 1px solid #e5e7eb;
    border-radius: 1.5mm;
    background: white;
    color: #111827;
  }
  .packing-label-cell-empty {
    color: transparent;
  }
  .packing-label-customer {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
    height: 100%;
    padding-right: var(--label-customer-padding-right, 2.2mm);
    border-right: 1px solid #e5e7eb;
    overflow: hidden;
  }
  .packing-label-customer-name {
    font-size: var(--label-customer-name-font-size, 33pt);
    line-height: var(--label-customer-line-height, 0.95);
    font-weight: 800;
    white-space: normal;
    overflow: visible;
    word-break: keep-all;
    overflow-wrap: anywhere;
  }
  .packing-label-phone {
    margin-top: var(--label-phone-margin-top, 1mm);
    font-size: var(--label-phone-font-size, 17pt);
    line-height: 1;
    font-weight: 800;
  }
  .packing-label-items {
    align-self: center;
    min-width: 0;
    font-size: var(--label-items-font-size, 10pt);
    line-height: var(--label-items-line-height, 1.22);
    font-weight: 700;
    word-break: break-word;
    display: block;
    overflow: visible;
    -webkit-line-clamp: unset;
    -webkit-box-orient: unset;
  }
  .packing-label-detail {
    align-self: center;
    min-width: 0;
    max-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
  }
  .packing-label-memo {
    margin-top: 0.8mm;
    font-size: 8.5pt;
    line-height: 1.1;
    font-weight: 800;
    color: #0369a1;
    word-break: break-word;
  }
  @media print {
    .report-content.packing-label-report {
      position: absolute !important;
      left: 0 !important;
      top: 0 !important;
      width: 210mm !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    .packing-label-area {
      display: block !important;
      width: 210mm !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
    }
    .packing-label-page {
      width: 210mm !important;
      height: 297mm !important;
      display: grid !important;
      grid-template-columns: 99.1mm 99.1mm !important;
      grid-auto-rows: 33.9mm !important;
      column-gap: 2.5mm !important;
      row-gap: 0 !important;
      padding: 14mm 4mm 10.4mm 4.8mm !important;
      box-sizing: border-box !important;
      margin: 0 !important;
      border: 0 !important;
      box-shadow: none !important;
      overflow: hidden !important;
      page-break-after: always;
      break-after: page;
    }
    .packing-label-page:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    .packing-label-cell {
      width: 99.1mm !important;
      height: 33.9mm !important;
      box-sizing: border-box !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
      align-items: stretch !important;
      column-gap: var(--label-column-gap, 3mm) !important;
      padding: var(--label-cell-padding, 2.6mm 4mm 2mm) !important;
      border: 1px solid #d1d5db !important;
      border-radius: 1.5mm !important;
      overflow: hidden !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .packing-label-customer {
      display: flex !important;
      flex-direction: column !important;
      justify-content: center !important;
      min-width: 0 !important;
      height: 100% !important;
      padding-right: var(--label-customer-padding-right, 2.2mm) !important;
      border-right: 1px solid #d1d5db !important;
      overflow: hidden !important;
    }
    .packing-label-customer-name {
      font-size: var(--label-customer-name-font-size, 33pt) !important;
      line-height: var(--label-customer-line-height, 0.95) !important;
      white-space: normal !important;
      word-break: keep-all !important;
      overflow-wrap: anywhere !important;
    }
    .packing-label-phone {
      margin-top: var(--label-phone-margin-top, 1mm) !important;
      font-size: var(--label-phone-font-size, 17pt) !important;
      line-height: 1 !important;
    }
    .packing-label-items {
      align-self: center !important;
      min-width: 0 !important;
      font-size: var(--label-items-font-size, 10pt) !important;
      line-height: var(--label-items-line-height, 1.22) !important;
      display: block !important;
      overflow: visible !important;
      padding-bottom: 1mm !important;
      -webkit-line-clamp: unset !important;
      -webkit-box-orient: unset !important;
    }
    .packing-label-detail {
      align-self: center !important;
      min-width: 0 !important;
      max-height: 100% !important;
      display: flex !important;
      flex-direction: column !important;
      justify-content: center !important;
      overflow: hidden !important;
    }
    .packing-label-memo {
      margin-top: 0.8mm !important;
      font-size: 8.5pt !important;
      line-height: 1.1 !important;
      color: #0369a1 !important;
    }
  }
  .a4-label-pdf-export {
    display: block !important;
    width: 210mm !important;
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }
  .a4-label-pdf-export .packing-label-area {
    display: block !important;
    width: 210mm !important;
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }
  .a4-label-pdf-export .packing-label-page {
    width: 210mm !important;
    height: 297mm !important;
    display: grid !important;
    grid-template-columns: 99.1mm 99.1mm !important;
    grid-auto-rows: 33.9mm !important;
    column-gap: 2.5mm !important;
    row-gap: 0 !important;
    padding: 14mm 4mm 10.4mm 4.8mm !important;
    box-sizing: border-box !important;
    margin: 0 !important;
    border: 0 !important;
    background: white !important;
    box-shadow: none !important;
    overflow: hidden !important;
    page-break-after: always;
    break-after: page;
  }
  .a4-label-pdf-export .packing-label-page:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  .a4-label-pdf-export .packing-label-cell {
    width: 99.1mm !important;
    height: 33.9mm !important;
    box-sizing: border-box !important;
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
    align-items: stretch !important;
    column-gap: var(--label-column-gap, 3mm) !important;
    padding: var(--label-cell-padding, 2.6mm 4mm 2mm) !important;
    border: 1px solid #d1d5db !important;
    border-radius: 1.5mm !important;
    background: white !important;
    color: #111827 !important;
    overflow: hidden !important;
  }
  .a4-label-pdf-export .packing-label-cell-empty {
    color: white !important;
    border-color: white !important;
  }
  .a4-label-pdf-export .packing-label-customer {
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
    min-width: 0 !important;
    height: 100% !important;
    padding-right: var(--label-customer-padding-right, 2.2mm) !important;
    border-right: 1px solid #d1d5db !important;
    overflow: hidden !important;
  }
  .a4-label-pdf-export .packing-label-customer-name {
    font-size: var(--label-customer-name-font-size, 33pt) !important;
    line-height: var(--label-customer-line-height, 0.95) !important;
    white-space: normal !important;
    word-break: keep-all !important;
    overflow-wrap: anywhere !important;
  }
  .a4-label-pdf-export .packing-label-phone {
    margin-top: var(--label-phone-margin-top, 1mm) !important;
    font-size: var(--label-phone-font-size, 17pt) !important;
    line-height: 1 !important;
  }
  .a4-label-pdf-export .packing-label-items {
    align-self: center !important;
    min-width: 0 !important;
    font-size: var(--label-items-font-size, 10pt) !important;
    line-height: var(--label-items-line-height, 1.22) !important;
    display: block !important;
    overflow: visible !important;
    padding-bottom: 1mm !important;
    -webkit-line-clamp: unset !important;
    -webkit-box-orient: unset !important;
  }
  .a4-label-pdf-export .packing-label-detail {
    align-self: center !important;
    min-width: 0 !important;
    max-height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
    overflow: hidden !important;
  }
  .a4-label-pdf-export .packing-label-memo {
    margin-top: 0.8mm !important;
    font-size: 8.5pt !important;
    line-height: 1.1 !important;
    color: #0369a1 !important;
  }
`;

const MAX_LABEL_SEGMENT_ITEMS = 8;
const MAX_LABEL_SEGMENT_TEXT_LENGTH = 145;
const MAX_LABEL_SEGMENT_TEXT_LENGTH_WITH_MEMO = 115;

const getLabelItemText = (item: any) =>
  `${getOrderItemDisplayName(item)}(${item.quantity})`;

const getPhoneLastFourDigits = (phone?: string) => {
  const digits = (phone || '').replace(/\D/g, '');

  if (digits.length >= 4) {
    return digits.slice(-4);
  }

  return phone || '연락처 없음';
};

const chunkLabels = <T,>(items: T[], size: number) => {
  const pages: T[][] = [];

  for (let index = 0; index < items.length; index += size) {
    pages.push(items.slice(index, index + size));
  }

  return pages;
};

const splitItemsForLabels = (items: any[], memoText: string) => {
  const segments: any[][] = [];
  let currentSegment: any[] = [];
  let currentTextLength = 0;

  const firstSegmentLimit = memoText
    ? MAX_LABEL_SEGMENT_TEXT_LENGTH_WITH_MEMO
    : MAX_LABEL_SEGMENT_TEXT_LENGTH;

  items.forEach((item) => {
    const itemText = getLabelItemText(item);
    const separatorLength = currentSegment.length > 0 ? 2 : 0;
    const nextLength = currentTextLength + separatorLength + itemText.length;
    const currentLimit = segments.length === 0
      ? firstSegmentLimit
      : MAX_LABEL_SEGMENT_TEXT_LENGTH;
    const shouldStartNextSegment =
      currentSegment.length > 0 &&
      (currentSegment.length >= MAX_LABEL_SEGMENT_ITEMS ||
        nextLength > currentLimit);

    if (shouldStartNextSegment) {
      segments.push(currentSegment);
      currentSegment = [];
      currentTextLength = 0;
    }

    currentSegment.push(item);
    currentTextLength +=
      (currentSegment.length > 1 ? 2 : 0) + itemText.length;
  });

  if (currentSegment.length > 0) {
    segments.push(currentSegment);
  }

  return segments.length > 0 ? segments : [items];
};

const expandLabelIntoSegments = (label: any) => {
  const itemSegments = splitItemsForLabels(label.items, label.memoText);

  return itemSegments.map((items, segmentIndex) => ({
    ...label,
    items,
    itemsText: items.map(getLabelItemText).join(', '),
    memoText: segmentIndex === 0 ? label.memoText : '',
    segmentIndex,
    segmentTotal: itemSegments.length,
  }));
};

const getLabelFitStyle = (label: any) => {
  const itemCount = label.items?.length || 0;
  const textLength =
    (label.itemsText?.length || 0) + (label.memoText?.length || 0);
  const nameLength = (label.customerName || '').replace(/\s/g, '').length;
  const nameStyle =
    nameLength > 18
      ? {
        '--label-customer-name-font-size': '14pt',
        '--label-phone-font-size': '10pt',
        '--label-phone-margin-top': '0.5mm',
      }
      : nameLength > 14
        ? {
          '--label-customer-name-font-size': '16pt',
          '--label-phone-font-size': '11pt',
          '--label-phone-margin-top': '0.6mm',
        }
        : nameLength > 10
          ? {
            '--label-customer-name-font-size': '19pt',
            '--label-phone-font-size': '13pt',
            '--label-phone-margin-top': '0.7mm',
          }
          : nameLength > 7
            ? {
              '--label-customer-name-font-size': '22pt',
              '--label-phone-font-size': '15pt',
              '--label-phone-margin-top': '0.8mm',
            }
            : {
              '--label-customer-name-font-size': '27pt',
              '--label-phone-font-size': '17pt',
              '--label-phone-margin-top': '1mm',
            };
  const withNameStyle = (style: Record<string, string>) => ({
    ...nameStyle,
    ...style,
  }) as React.CSSProperties;

  if (textLength > 500 || itemCount > 25) {
    return withNameStyle({
      '--label-cell-padding': '1.5mm 2.8mm 1.2mm',
      '--label-column-gap': '2mm',
      '--label-customer-line-height': '0.92',
      '--label-items-font-size': '6.8pt',
      '--label-items-line-height': '1.06',
    });
  }

  if (textLength > 340 || itemCount > 18) {
    return withNameStyle({
      '--label-cell-padding': '1.6mm 3mm 1.3mm',
      '--label-column-gap': '2.2mm',
      '--label-customer-line-height': '0.93',
      '--label-items-font-size': '7.2pt',
      '--label-items-line-height': '1.08',
    });
  }

  if (textLength > 260 || itemCount > 14) {
    return withNameStyle({
      '--label-cell-padding': '1.8mm 3.1mm 1.4mm',
      '--label-column-gap': '2.3mm',
      '--label-customer-line-height': '0.94',
      '--label-items-font-size': '7.8pt',
      '--label-items-line-height': '1.1',
    });
  }

  if (textLength > 190 || itemCount > 10) {
    return withNameStyle({
      '--label-cell-padding': '2mm 3.3mm 1.6mm',
      '--label-column-gap': '2.5mm',
      '--label-customer-line-height': '0.94',
      '--label-items-font-size': '8.3pt',
      '--label-items-line-height': '1.12',
    });
  }

  if (textLength > 130 || itemCount > 7) {
    return withNameStyle({
      '--label-cell-padding': '2.2mm 3.4mm 1.8mm',
      '--label-column-gap': '2.7mm',
      '--label-customer-line-height': '0.95',
      '--label-items-font-size': '8.8pt',
      '--label-items-line-height': '1.15',
    });
  }

  if (textLength > 90 || itemCount > 5) {
    return withNameStyle({
      '--label-cell-padding': '2.4mm 3.6mm 2mm',
      '--label-column-gap': '2.8mm',
      '--label-customer-line-height': '0.95',
      '--label-items-font-size': '9.3pt',
      '--label-items-line-height': '1.17',
    });
  }

  return withNameStyle({
    '--label-cell-padding': '2.6mm 4mm 2mm',
    '--label-column-gap': '3mm',
    '--label-customer-line-height': '0.95',
    '--label-items-font-size': '10pt',
    '--label-items-line-height': '1.22',
  });
};

interface PackingLabelsViewProps {
  data: any[];
  customerAliases?: Record<string, string>;
  orderSheets?: OrderSheet[];
  sortBy?: 'default' | 'name';
}

export const PackingLabelsView = ({
  data,
  customerAliases,
  orderSheets,
  sortBy,
}: PackingLabelsViewProps) => {
  const labels = useMemo(() => {
    if (!data) return [];

    const map: Record<string, any> = {};

    data
      .filter((order) => order.status !== 'CANCELLED')
      .forEach((order) => {
        const phoneDigits = (order.customerPhone || '').replace(/\D/g, '');
        const sheetId = order.orderSheetId || order.storeId || '';
        const name = (order.customerName || '').trim();
        const key = `${sheetId}-${name}-${phoneDigits}`;
        const createdAtTime = order.createdAt ? new Date(order.createdAt).getTime() : 0;

        if (!map[key]) {
          map[key] = {
            ...order,
            items: [...(order.items || [])],
            sellerMemo: order.sellerMemo?.trim() || '',
            latestCreatedAtTime: createdAtTime,
          };
          return;
        }

        map[key].items = [...map[key].items, ...(order.items || [])];
        const sellerMemo = order.sellerMemo?.trim();
        if (
          sellerMemo &&
          !String(map[key].sellerMemo || '').includes(sellerMemo)
        ) {
          map[key].sellerMemo = map[key].sellerMemo
            ? `${map[key].sellerMemo}\n${sellerMemo}`
            : sellerMemo;
        }
        map[key].latestCreatedAtTime = Math.max(
          map[key].latestCreatedAtTime || 0,
          createdAtTime,
        );
      });

    const aggregated = Object.values(map)
      .map((order: any) => {
        const itemMap: Record<string, any> = {};

        order.items.forEach((item: any) => {
          if (item.quantity <= 0) return;

          const itemKey = getOrderItemAggregationKey(item);
          if (!itemMap[itemKey]) {
            itemMap[itemKey] = { ...item };
          } else {
            itemMap[itemKey].quantity += item.quantity;
          }
        });

        const items = Object.values(itemMap);
        const phoneDigits = (order.customerPhone || '').replace(/\D/g, '');
        const alias = phoneDigits ? customerAliases?.[phoneDigits] : undefined;
        const customerName = alias
          ? `${alias} (${order.customerName})`
          : order.customerName || '고객명 없음';
        const orderSheet = orderSheets?.find((sheet) => sheet.id === order.orderSheetId);
        const memoText = order.sellerMemo?.trim() || '';

        return {
          ...order,
          customerName,
          phoneLastFourDigits: getPhoneLastFourDigits(order.customerPhone),
          items,
          orderSheetLabel: orderSheet?.label,
          itemsText: items.map(getLabelItemText).join(', '),
          memoText,
        };
      })
      .filter((order: any) => order.items.length > 0);

    if (sortBy === 'name') {
      return aggregated.sort((a: any, b: any) =>
        (a.customerName || '').localeCompare(b.customerName || '', 'ko-KR'),
      ).flatMap(expandLabelIntoSegments);
    }

    return aggregated.sort((a: any, b: any) =>
      (b.latestCreatedAtTime || 0) - (a.latestCreatedAtTime || 0),
    ).flatMap(expandLabelIntoSegments);
  }, [data, customerAliases, orderSheets, sortBy]);

  const pages = useMemo(() => chunkLabels(labels, LABELS_PER_PAGE), [labels]);

  if (labels.length === 0) {
    return (
      <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
        <FileText size={48} strokeWidth={1} />
        <p className="text-sm font-bold">출력할 포장 라벨이 없습니다.</p>
        <p className="text-xs">선택한 주문서에 취소되지 않은 주문이 있으면 라벨이 표시됩니다.</p>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PACKING_LABEL_STYLES }} />
      <div className="packing-label-area">
        {pages.map((pageLabels, pageIndex) => {
          const emptyCount = Math.max(LABELS_PER_PAGE - pageLabels.length, 0);

          return (
            <div key={pageIndex} className="packing-label-page">
              {pageLabels.map((label: any, index: number) => (
                <div
                  key={`${label.orderSheetId || 'sheet'}-${label.customerName}-${label.customerPhone || index}-${label.segmentIndex || 0}`}
                  className="packing-label-cell"
                  style={getLabelFitStyle(label)}
                >
                  <div className="packing-label-customer">
                    <div className="packing-label-customer-name">{label.customerName}</div>
                    <div className="packing-label-phone">
                      ({label.phoneLastFourDigits})
                      {label.segmentTotal > 1 ? ` ${label.segmentIndex + 1}/${label.segmentTotal}` : ''}
                    </div>
                  </div>
                  <div className="packing-label-detail">
                    <div className="packing-label-items">
                      {label.itemsText}
                    </div>
                    {label.memoText && (
                      <div className="packing-label-memo">
                        메모: {label.memoText}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {Array.from({ length: emptyCount }, (_, index) => (
                <div
                  key={`empty-${pageIndex}-${index}`}
                  className="packing-label-cell packing-label-cell-empty"
                  aria-hidden="true"
                />
              ))}
            </div>
          );
        })}
      </div>
    </>
  );
};
